import './fonts';
import './bootstrap';
import '../../css/app.css';
import '@xyflow/react/dist/style.css';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-toastify/dist/ReactToastify.css';

import NProgress from 'nprogress';
import Cookies from 'universal-cookie';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { createInertiaApp, router } from '@inertiajs/react';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { ToastContainer } from 'react-toastify';
import { Provider } from 'react-redux';
import * as Sentry from '@sentry/react';
import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react';

// Providers
import { ApplicationProvider } from './provider/Application';
import { AppModalProvider } from './provider/AppModal';
import { BottomSheetProvider } from './provider/BottomSheet';

// Redux
import { store } from './store';

// Analytics
import analytics from './analytics';
import { clarity } from 'react-microsoft-clarity';

Sentry.init({
    dsn: 'https://fbc84dcea9876bbb9fe4c5df16563034@o4507311208136704.ingest.de.sentry.io/4508993522368592',
    enabled: import.meta.env.PROD,
    debug: import.meta.env.DEV,
    environment: import.meta.env.DEV ? 'development' : 'production',
});

const appName = import.meta.env.VITE_APP_NAME || 'Laravel';

createInertiaApp({
    title: (title) => {
        if (
            title.startsWith('ReelFlow Dashboard') ||
            title.startsWith('ReelFlow Login') ||
            title.startsWith('ReelFlow Forgot')
        ) {
            return title;
        }

        return `${title} – ${appName}`;
    },
    resolve: (name) => {
        return resolvePageComponent(
            `./Pages/${name}.tsx`,
            import.meta.glob('./Pages/**/*.tsx'),
        );
    },
    setup({ el, App, props }) {
        const content = (
            <Provider store={store}>
                <ApplicationProvider>
                    <AppModalProvider>
                        <BottomSheetProvider>
                            <SentryErrorBoundary fallback={undefined}>
                                <App {...props} />
                                <ToastContainer />
                            </SentryErrorBoundary>
                        </BottomSheetProvider>
                    </AppModalProvider>
                </ApplicationProvider>
            </Provider>
        );

        if (import.meta.env.DEV) {
            createRoot(el).render(content);

            return;
        }

        hydrateRoot(el, content);
    },
    // NOTE: Hide the default progress bar since we implement custom one
    progress: false,
});

NProgress.configure({
    template:
        '<div class="bar !bg-brand-ocean !opacity-100" role="bar" style="height:3px"><div class="peg"></div></div>',
});

router.on('start', (event) => {
    NProgress.start();
});

router.on('finish', (event) => {
    NProgress.done();
});

router.on('success', (event) => {
    // @ts-ignore
    if (event.detail.page && process.env.NODE_ENV === 'production') {
        analytics.page();

        const isClarityInitialized = clarity.hasStarted();
        // @ts-expect-error
        const user = event.detail.page.props?.auth?.user;

        if (user && !isClarityInitialized) {
            clarity.init(import.meta.env.VITE_MICROSOFT_CLARITY_ID);

            const consentAnalytics = user.user_tracking_enabled
                ? 'granted'
                : 'denied';

            window.clarity?.('consentv2', {
                ad_Storage: 'denied',
                analytics_Storage: consentAnalytics,
            });

            const isStaging = window?.location?.origin?.includes('staging');
            const userEmail = user.email;

            const userType = !userEmail
                ? 'unknown'
                : userEmail.includes('@reelflow')
                  ? 'reelflow_internal'
                  : 'not_reelflow_internal';

            clarity.setTag('env', isStaging ? 'staging' : 'production');
            clarity.setTag('user_type', userType);
        }
    }

    const cookieMap = new Cookies(document.cookie);

    cookieMap.set('reelflow_ref_count', '1', { path: '/' });
});

router.on('navigate', () => {
    const cookieMap = new Cookies(document.cookie);

    cookieMap.set('reelflow_ref_count', '1', { path: '/' });
});
